// MainScene - Game initialization and start scene
import Phaser from 'phaser';
import { useGameStore } from '../../store/gameStore';
import { loadAllCards, getPlayableFactions, loadFactionCards } from '../../services/CardLoader';
import { preloadDeckImages } from '../../services/CardImageLoader';
import type { CardDefinition } from '../../types';
import { getLogger } from '../../utils/logger';

const log = getLogger('MainScene');

export class MainScene extends Phaser.Scene {
  private startButton!: Phaser.GameObjects.Container;
  private isInitializing = false;

  constructor() {
    super({ key: 'MainScene' });
  }

  preload(): void {
    // Load background and UI assets
    this.load.image('starry-bg', 'assets/backgrounds/starry-bg.jpg');
    this.load.image('logo', 'assets/ui/logo.png');
  }

  create(): void {
    const { width, height } = this.cameras.main;

    // Add starry background
    if (this.textures.exists('starry-bg')) {
      const bg = this.add.image(width / 2, height / 2, 'starry-bg');
      const scaleX = width / bg.width;
      const scaleY = height / bg.height;
      const scale = Math.max(scaleX, scaleY);
      bg.setScale(scale);
    }

    // Title with logo
    const titleY = height / 3;
    if (this.textures.exists('logo')) {
      const logo = this.add.image(width / 2, titleY - 30, 'logo');
      logo.setScale(0.6);
    } else {
      // Fallback text title
      const title = this.add.text(width / 2, titleY, '星靈王', {
        fontSize: '64px',
        color: '#ffffff',
        fontStyle: 'bold',
      });
      title.setOrigin(0.5, 0.5);
    }

    // Subtitle
    const subtitle = this.add.text(width / 2, titleY + 80, 'Xinglingwang Card Battle', {
      fontSize: '24px',
      color: '#aaccff',
    });
    subtitle.setOrigin(0.5, 0.5);

    // Start button
    this.createStartButton(width / 2, height / 2 + 50);

    // Version info
    const version = this.add.text(width - 10, height - 10, 'v0.1.0 - 開發中', {
      fontSize: '12px',
      color: '#666666',
    });
    version.setOrigin(1, 1);

    // Card count info
    const allCards = loadAllCards();
    const cardInfo = this.add.text(10, height - 10, `已載入 ${String(allCards.size)} 張卡牌`, {
      fontSize: '12px',
      color: '#666666',
    });
    cardInfo.setOrigin(0, 1);

    // Auto-start the game after a short delay (skip manual click)
    this.time.delayedCall(500, () => {
      void this.startGame();
    });
  }

  private createStartButton(x: number, y: number): void {
    this.startButton = this.add.container(x, y);

    const bg = this.add.rectangle(0, 0, 200, 50, 0x4a4a8a);
    bg.setStrokeStyle(2, 0x6a6aaa);
    bg.setInteractive({ useHandCursor: true });

    const text = this.add.text(0, 0, '開始遊戲', {
      fontSize: '24px',
      color: '#ffffff',
    });
    text.setOrigin(0.5, 0.5);

    this.startButton.add([bg, text]);

    bg.on('pointerover', () => {
      bg.setFillStyle(0x6a6aaa);
    });

    bg.on('pointerout', () => {
      bg.setFillStyle(0x4a4a8a);
    });

    bg.on('pointerdown', () => {
      void this.startGame();
    });
  }

  private async startGame(): Promise<void> {
    if (this.isInitializing) return;
    this.isInitializing = true;

    // Show loading text
    const { width, height } = this.cameras.main;
    const loadingText = this.add.text(width / 2, height / 2 + 120, '初始化遊戲...', {
      fontSize: '18px',
      color: '#ffcc00',
    });
    loadingText.setOrigin(0.5, 0.5);

    try {
      // Load all card definitions
      const allCards = loadAllCards();
      const cardDefinitions = Array.from(allCards.values());

      const gameStore = useGameStore.getState();
      gameStore.setCardDefinitions(cardDefinitions);

      // Online mode: server already set gameState via startOnlineGame — skip local initGame
      const isOnline = gameStore.gameMode === 'online' && gameStore.onlineGameId;

      if (!isOnline) {
        // Local mode: initialize local game state
        const factions = getPlayableFactions();
        const virtualFaction = factions.find(f => f.id === 'virtual') || factions[0];
        const travelerFaction = factions.find(f => f.id === 'traveler') || factions[1];
        const randomFaction1 = virtualFaction;
        const randomFaction2 = travelerFaction;

        const faction1Cards = loadFactionCards(randomFaction1.id as 'fairy' | 'robot' | 'skeleton');
        const faction2Cards = loadFactionCards(randomFaction2.id as 'fairy' | 'robot' | 'skeleton');
        const deck1 = this.createDemoDeck(faction1Cards);
        const deck2 = this.createDemoDeck(faction2Cards);

        gameStore.initGame(
          {
            id: 'player1',
            name: '玩家',
            faction: randomFaction1.id,
            deck: deck1,
          },
          {
            id: 'player2',
            name: '對手',
            faction: randomFaction2.id,
            deck: deck2,
          }
        );

        loadingText.setText(`玩家: ${randomFaction1.nameZh} vs 對手: ${randomFaction2.nameZh}`);
      } else {
        loadingText.setText('連線對戰載入中...');
        log.debug('Online mode: skipping local initGame, using server state');
      }

      // Preload card images
      loadingText.setText('載入卡牌圖片...');
      let cardsToLoad: CardDefinition[];
      if (isOnline) {
        // Online mode: load images for all visible cards in current state + full card pool
        // (opponent cards may be revealed later; preload broadly)
        cardsToLoad = cardDefinitions;
      } else {
        // Local mode: just the two decks
        const gs = gameStore.gameState;
        if (gs) {
          const d1 = gs.player1.deck.map(c => allCards.get(c.definitionId)).filter(Boolean) as CardDefinition[];
          const d2 = gs.player2.deck.map(c => allCards.get(c.definitionId)).filter(Boolean) as CardDefinition[];
          const h1 = gs.player1.hand.map(c => allCards.get(c.definitionId)).filter(Boolean) as CardDefinition[];
          const h2 = gs.player2.hand.map(c => allCards.get(c.definitionId)).filter(Boolean) as CardDefinition[];
          cardsToLoad = [...d1, ...d2, ...h1, ...h2];
        } else {
          cardsToLoad = [];
        }
      }
      const loadResult = await preloadDeckImages(this, cardsToLoad, (loaded, total) => {
        loadingText.setText(`載入卡牌圖片... ${String(loaded)}/${String(total)}`);
      });

      log.debug(`Loaded ${String(loadResult.loaded)} card images, ${String(loadResult.failed)} failed`);

      // Wait a moment then start battle
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Transition to battle scene
      this.scene.start('BattleScene');

    } catch (error) {
      log.error('Failed to initialize game:', error);
      loadingText.setText('初始化失敗，請重試');
      loadingText.setColor('#ff4444');
      this.isInitializing = false;
    }
  }

  private createDemoDeck(factionCards: CardDefinition[]): CardDefinition[] {
    const deck: CardDefinition[] = [];
    const unitCards = factionCards.filter((c) => c.type === 'unit');

    // Fill deck with up to 20 cards (with duplicates allowed up to 3)
    const cardCounts = new Map<string, number>();

    while (deck.length < 20 && unitCards.length > 0) {
      const randomIndex = Math.floor(Math.random() * unitCards.length);
      const card = unitCards[randomIndex];
      const count = cardCounts.get(card.id) || 0;

      if (count < 3) {
        deck.push(card);
        cardCounts.set(card.id, count + 1);
      }

      // If we've used all cards to their limit, break
      if (deck.length >= 20) break;

      // Prevent infinite loop
      let allMaxed = true;
      for (const c of unitCards) {
        if ((cardCounts.get(c.id) || 0) < 3) {
          allMaxed = false;
          break;
        }
      }
      if (allMaxed) break;
    }

    // If we still don't have 20 cards, fill with neutral units
    // For now, just duplicate what we have
    while (deck.length < 20 && deck.length > 0) {
      const randomIndex = Math.floor(Math.random() * deck.length);
      deck.push(deck[randomIndex]);
    }

    return deck.slice(0, 20);
  }
}
